/**
 * F12 Sprint E — CopilotProvider (item #327)
 *
 * Mounts globally:
 * 1. Registers the Cmd+I / Ctrl+I keyboard shortcut to toggle the palette.
 *    (Cmd+J is now owned by CommandPalette — global entity search, #10 Faz 1.
 *     Cmd+K is left free for the browser; Ctrl+I is not a reserved browser combo.)
 * 2. Renders <CopilotPalette> (modal, shown when isOpen=true).
 *
 * Usage: wrap around the layout root in App.tsx.
 * <CopilotSidebarPin> is rendered separately inside the layout flex row.
 */

import { useEffect } from 'react';
import { useCopilotStore } from '../../stores/copilotStore.js';
import CopilotPalette from './CopilotPalette.js';

export default function CopilotProvider({ children }: { children: React.ReactNode }) {
  const { toggle, isPinned, open } = useCopilotStore();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Cmd+I / Ctrl+I — Copilot AI chat toggle (Cmd+J is now global search)
      if (modifier && e.key === 'i') {
        // Only bail inside a rich-text (contenteditable) surface — there Ctrl+I is
        // the italic combo. Plain <input>/<textarea> don't italicize, so Copilot may
        // still open from them (symmetric with the CommandPalette Ctrl+J launcher,
        // which has no input guard either).
        const el = document.activeElement as HTMLElement | null;
        if (el?.isContentEditable) return;

        e.preventDefault();
        if (isPinned) {
          // If pinned, Cmd+I focuses the sidebar instead of opening modal
          open();
        } else {
          toggle();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle, isPinned, open]);

  return (
    <>
      {children}
      <CopilotPalette />
    </>
  );
}
